import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type ConfirmTone = "default" | "danger" | "warn";

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
};

type ConfirmRequest = ConfirmOptions & {
  id: string;
  resolve: (value: boolean) => void;
};

const Ctx = createContext<{
  confirm: (opts: ConfirmOptions | string) => Promise<boolean>;
} | null>(null);

function normalize(opts: ConfirmOptions | string): ConfirmOptions {
  if (typeof opts === "string") return { message: opts };
  return opts;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<ConfirmRequest[]>([]);
  const active = queue[0] || null;
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);

  const confirm = useCallback((opts: ConfirmOptions | string) => {
    const normalized = normalize(opts);
    return new Promise<boolean>((resolve) => {
      setQueue((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          ...normalized,
          resolve,
        },
      ]);
    });
  }, []);

  const settle = useCallback((id: string, value: boolean) => {
    setQueue((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item) item.resolve(value);
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  useEffect(() => {
    if (!active) {
      document.documentElement.classList.remove("ms-confirm-open");
      document.body.classList.remove("ms-confirm-open");
      document.body.style.removeProperty("overflow");
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === "function") {
        try {
          prev.focus({ preventScroll: true });
        } catch {
          /* ignore */
        }
      }
      prevFocusRef.current = null;
      return;
    }

    prevFocusRef.current = document.activeElement as HTMLElement | null;
    document.documentElement.classList.add("ms-confirm-open");
    document.body.classList.add("ms-confirm-open");
    document.body.style.overflow = "hidden";

    const t = window.setTimeout(() => confirmBtnRef.current?.focus(), 30);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        settle(active.id, false);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [active, settle]);

  const value = useMemo(() => ({ confirm }), [confirm]);
  const tone = active?.tone || "default";

  const dialog =
    active &&
    createPortal(
      <div
        className="confirm-back"
        role="presentation"
        onClick={() => settle(active.id, false)}
      >
        <div
          className={`confirm-dialog confirm-${tone}`}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={`confirm-title-${active.id}`}
          aria-describedby={`confirm-msg-${active.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="confirm-eyebrow">
            {tone === "danger" ? "Attenzione" : tone === "warn" ? "Conferma" : "Conferma"}
          </div>
          <h2 id={`confirm-title-${active.id}`} className="confirm-title">
            {active.title || "Confermi?"}
          </h2>
          <p id={`confirm-msg-${active.id}`} className="confirm-message">
            {active.message}
          </p>
          <div className="confirm-actions">
            <button type="button" className="btn" onClick={() => settle(active.id, false)}>
              {active.cancelLabel || "Annulla"}
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              className={`btn ${tone === "danger" ? "btn-danger" : tone === "warn" ? "btn-warn" : "btn-primary"}`}
              onClick={() => settle(active.id, true)}
            >
              {active.confirmLabel || "Conferma"}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <Ctx.Provider value={value}>
      {children}
      {dialog}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("ConfirmProvider missing");
  return ctx;
}
